import React, { memo, useState } from "react";
import { Modal, View, StyleSheet, Text, Pressable, TextInput, ActivityIndicator, Alert } from "react-native";
import { AntDesign } from "@expo/vector-icons";
import { addDoc, collection } from "firebase/firestore";
import { uploadBytes, ref, getDownloadURL } from "firebase/storage";
import * as DocumentPicker from 'expo-document-picker';

import { db, storage } from "../services/connect";
import RenderIf from "./RenderIf";
import { colors } from "./Shared";
import { CandidateProps } from "../types/candidate";

interface ModalResourceProps {
	visible: boolean;
	close: () => void;
}
const ModalResource = ({ visible, close }: ModalResourceProps) => {
	const [data, setData] = useState<CandidateProps>({} as CandidateProps);
	const [saving, setSaving] = useState(false);
	const [uploadingCv, setUploadingCv] = useState(false);

	async function save() {
		if (!data.profession || !data.location || !data.name) {
			return alert('Please fill in all the fields.')
		}
		
		if (!data.resumeUrl) {
			return alert('Please choose a CV file.')
		}
	
		setSaving(true);
		const ref = collection(db, 'candidates');
		await addDoc(ref, data);
		setSaving(false);
		close();
		setData({} as CandidateProps);
	}

	async function pickDocument() {
		try {
			setUploadingCv(true);
			const results = await DocumentPicker.getDocumentAsync();
			if (results.type == 'cancel') {
				return;
			}
	
			const { file, name } = results;
			const docRef = ref(storage, 'documents/' + name);
			const uploaded = await uploadBytes(docRef, file!);
			const url = await getDownloadURL(uploaded.ref);
			setData({
				...data,
				resumeUrl: url,
				resumeDocName: name
			});
		} catch (error) {
			Alert.alert('Error', 'An unknown error occurred while uploading the file');
			throw error;
		} finally {
			setUploadingCv(false);
		}
	}

	return (
		<Modal
			visible={visible}
			transparent={true}
			animationType='fade'
		>
			<View style={styles.backdrop}>
				<View style={styles.container}>
					<View style={styles.header}>
						<Text style={{ fontSize: 20 }}>New Resource</Text>

						<Pressable onPress={close}>
							<AntDesign name="closecircleo" size={24} color="black" />
						</Pressable>
					</View>
					
					<View
						style={{
							height: 1,
							backgroundColor: '#000',
							opacity: 0.1,
							marginVertical: 20
						}}
					/>

					<Input
						label='Name'
						onChange={(name) => setData({ ...data, name })}
					/>
					<Input
						label='Profession'
						onChange={(profession) => setData({ ...data, profession })}
					/>
					<Input
						label='Location'
						onChange={(location) => setData({ ...data, location })}
					/>
					<Input
						label='Recruitment company'
						onChange={(recruitmentCompany) => setData({ ...data, recruitmentCompany })}
					/>


					<RenderIf condition={!!data.resumeDocName}>
						<View style={styles.fileContainer}>
							<View style={{ flexDirection: 'row', alignItems: 'center' }}>
								<AntDesign name="filetext1" size={24} color="black" />
								<Text style={{ maxWidth: 150, marginLeft: 10 }}>{data.resumeDocName}</Text>
							</View>

							<Pressable onPress={() => setData({ ...data, resumeDocName: undefined, resumeUrl: '' })}>
								<AntDesign name="closecircleo" size={24} color="black" />
							</Pressable>
						</View>
					</RenderIf>

					<RenderIf condition={!data.resumeDocName}>
						<Pressable
							onPress={pickDocument}
							style={styles.chooseFileBtn}
						>
							<RenderIf condition={uploadingCv}>
								<ActivityIndicator color={colors.bg.primary} />
							</RenderIf>

							<RenderIf condition={!uploadingCv}>
								<Text>Choose CV</Text>
							</RenderIf>
						</Pressable>
					</RenderIf>

					<Pressable style={styles.saveBtnContainer} onPress={save}>
						<RenderIf condition={saving}>
							<ActivityIndicator size='small' color='#fff' />
						</RenderIf>

						<RenderIf condition={!saving}>
							<Text style={styles.saveBtnText}>Save</Text>
						</RenderIf>
					</Pressable>
				</View>
			</View>
		</Modal>
	)
}

interface InputProps {
	label: string;
	onChange?: (text: string) => void;
}
const Input = ({ label, onChange }: InputProps) => (
	<View style={{ marginVertical: 10 }}>
		<Text>{label}</Text>
		<TextInput
			style={styles.input}
			onChangeText={onChange}
		/>
	</View>
)

const styles = StyleSheet.create({
	backdrop: {
		justifyContent: 'center',
		alignItems: 'center',
		height: '100%',
		backgroundColor: 'rgba(0, 0, 0, 0.5)'
	},
	container: {
		backgroundColor: '#fff',
		width: 300,
		borderRadius: 10,
		shadowColor: '#000',
		shadowOffset: {
			width: 0,
			height: 0
		},
		shadowOpacity: 0.1,
		shadowRadius: 40,
		padding: 20
	},
	header: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between'
	},
	input: {
		backgroundColor: 'rgba(0, 0, 0, .05)',
		borderRadius: 8,
		padding: 10,
		marginTop: 5
	},
	saveBtnContainer: {
		backgroundColor: colors.bg.primary,
		padding: 10,
		borderRadius: 8,
		marginTop: 30
	},
	saveBtnText: {
		color: '#fff',
		textAlign: 'center'
	},
	chooseFileBtn: {
		width: '100%',
		padding: 10,
		justifyContent: 'center',
		borderRadius: 5,
		borderWidth: 1,
		borderColor: colors.bg.primary,
		marginTop: 20
	},
	fileContainer: {
		width: '100%',
		padding: 10,
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		borderRadius: 5,
		borderWidth: 1,
		borderColor: 'rgba(0, 0, 0, .5)',
		marginTop: 20
	}
});

export default memo(ModalResource);
